import { useMemo, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { FarmerRegister, FarmHolding, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const PRODUCTION_TYPES = ['CROP_FARMING', 'LIVESTOCK', 'POULTRY', 'DAIRY', 'APICULTURE', 'HORTICULTURE', 'MIXED_FARMING'] as const;
const LIST_ROLES = ['AGRI_OFFICER', 'SCHEME_ADMIN'];

/** M33 Agriculture — farmer register, one-stop identity card, and C9 GeoServe-verified land holdings. */
export function FarmersHoldings() {
  const { persona } = useAuth();
  const isAgriOfficer = persona ? persona.roles.includes('AGRI_OFFICER') : false;
  const canList = persona ? persona.roles.some((r) => LIST_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m33/farmers?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<FarmerRegister>>(path);

  return (
    <div>
      <PageHeader
        title="Farmers & Holdings"
        subtitle="Farmer register with SFA-style identity card, and C9 GeoServe-verified land holdings (M33)."
        actions={
          isAgriOfficer ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + Register farmer
            </button>
          ) : undefined
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-56" placeholder="Farmer no, name…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {['PENDING_PAYMENT', 'ACTIVE', 'SUSPENDED', 'REVOKED', 'REJECTED', 'WITHDRAWN'].map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
      </div>

      {!canList ? <RoleNotice roles={LIST_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No farmer registrations match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Farmer No</th>
                <th className="table-head">Name</th>
                <th className="table-head">District</th>
                <th className="table-head">Production</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((f) => (
                <tr key={f.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(f.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{f.farmerNo}</td>
                  <td className="table-cell">{f.fullName}</td>
                  <td className="table-cell text-xs text-slate-500">{f.district}</td>
                  <td className="table-cell text-xs text-slate-500">{f.productionTypes.map((t) => t.replaceAll('_', ' ')).join(', ')}</td>
                  <td className="table-cell">
                    <StatusBadge status={f.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <FarmerDetailModal
          id={selectedId}
          isAgriOfficer={isAgriOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}
      {showCreate ? (
        <NewFarmerModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function FarmerDetailModal({ id, isAgriOfficer, onClose, onChanged }: { id: string; isAgriOfficer: boolean; onClose: () => void; onChanged: () => void }) {
  const fq = useApiQuery<FarmerRegister>(`/api/v1/m33/farmers/${id}`);
  const holdingsQ = useApiQuery<{ items: FarmHolding[] }>(`/api/v1/m33/farmers/${id}/holdings`);
  const f = fq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReason, setShowReason] = useState<'suspend' | 'revoke' | null>(null);
  const [reason, setReason] = useState('');
  const [showAddHolding, setShowAddHolding] = useState(false);
  const [parcelNo, setParcelNo] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const reinstate = () => call(() => api.post(`/api/v1/m33/farmers/${id}/reinstate`));
  const withdraw = () => call(() => api.post(`/api/v1/m33/farmers/${id}/withdraw`));
  const confirmReasoned = () => call(() => api.post(`/api/v1/m33/farmers/${id}/${showReason}`, { reason }));

  const addHolding = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m33/farmers/${id}/holdings`, { parcelNo });
      setParcelNo('');
      setShowAddHolding(false);
      holdingsQ.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={f ? `${f.farmerNo} — ${f.fullName}` : 'Farmer registration'} onClose={onClose} wide>
      {fq.error ? <ErrorNote message={fq.error} /> : null}
      {fq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {f ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={f.status} />} />
            <Detail label="Applicant" value={`${f.applicantType}:${f.applicantId}`} mono />
            <Detail label="Phone" value={f.phone ?? '—'} />
            <Detail label="District" value={f.district} />
            <Detail label="Production types" value={f.productionTypes.map((t) => t.replaceAll('_', ' ')).join(', ')} />
            <Detail label="Card (DocVault)" value={f.cardDocVaultRef ?? '—'} mono />
          </div>
          {f.rejectionReason ? <ErrorNote message={`Rejected: ${f.rejectionReason}`} /> : null}
          {f.suspendedReason ? <ErrorNote message={`Suspended: ${f.suspendedReason}`} /> : null}
          {f.revokedReason ? <ErrorNote message={`Revoked: ${f.revokedReason}`} /> : null}

          <div>
            <div className="mb-1 flex items-center justify-between">
              <div className="field-label !mb-0">Land holdings (C9 GeoServe-verified)</div>
              <button className="text-xs font-semibold text-brand-700 underline" onClick={() => setShowAddHolding((s) => !s)}>
                + Add holding
              </button>
            </div>
            {showAddHolding ? (
              <div className="mb-2 flex gap-2">
                <input className="field-input" placeholder="Parcel no." value={parcelNo} onChange={(e) => setParcelNo(e.target.value)} />
                <button className="btn-primary" disabled={busy || !parcelNo} onClick={addHolding}>
                  Verify &amp; add
                </button>
              </div>
            ) : null}
            {holdingsQ.loading ? (
              <Spinner />
            ) : (holdingsQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-sm text-slate-400">No holdings registered.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="table-head">Parcel</th>
                    <th className="table-head">District</th>
                    <th className="table-head">Area</th>
                    <th className="table-head">Tenure</th>
                  </tr>
                </thead>
                <tbody>
                  {holdingsQ.data!.items.map((h) => (
                    <tr key={h.id}>
                      <td className="table-cell font-mono text-xs">{h.parcelNo}</td>
                      <td className="table-cell text-xs">{h.district}</td>
                      <td className="table-cell text-xs">{Number(h.areaGeo).toLocaleString()}</td>
                      <td className="table-cell text-xs">{h.tenureType.replaceAll('_', ' ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {isAgriOfficer ? (
            <div className="border-t border-slate-100 pt-4">
              {!showReason ? (
                <div className="flex flex-wrap gap-2">
                  {f.status === 'ACTIVE' ? (
                    <button className="btn-secondary" disabled={busy} onClick={() => setShowReason('suspend')}>
                      Suspend
                    </button>
                  ) : null}
                  {f.status === 'SUSPENDED' ? (
                    <button className="btn-success" disabled={busy} onClick={reinstate}>
                      Reinstate
                    </button>
                  ) : null}
                  {f.status === 'ACTIVE' || f.status === 'SUSPENDED' ? (
                    <button className="btn-danger" disabled={busy} onClick={() => setShowReason('revoke')}>
                      Revoke
                    </button>
                  ) : null}
                  {f.status === 'PENDING_PAYMENT' ? (
                    <button className="btn-secondary" disabled={busy} onClick={withdraw}>
                      Withdraw
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <Field label="Reason">
                    <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowReason(null)}>
                      Cancel
                    </button>
                    <button className={showReason === 'revoke' ? 'btn-danger' : 'btn-secondary'} disabled={busy || reason.trim().length < 3} onClick={confirmReasoned}>
                      {busy ? 'Submitting…' : `Confirm ${showReason}`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function NewFarmerModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [applicantType, setApplicantType] = useState<'PERSON' | 'ENTITY'>('PERSON');
  const [applicantId, setApplicantId] = useState('');
  const [fullName, setFullName] = useState('');
  const [district, setDistrict] = useState('Grand Anse Mahe');
  const [phone, setPhone] = useState('');
  const [productionTypes, setProductionTypes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (v: string) => setProductionTypes((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m33/farmers', { applicantType, applicantId, fullName, district, phone: phone || undefined, productionTypes });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register farmer" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Applicant type">
            <select className="field-input" value={applicantType} onChange={(e) => setApplicantType(e.target.value as 'PERSON' | 'ENTITY')}>
              <option value="PERSON">Person (NIN)</option>
              <option value="ENTITY">Entity (business ID)</option>
            </select>
          </Field>
          <Field label={applicantType === 'PERSON' ? 'NIN' : 'Business ID'}>
            <input className="field-input" value={applicantId} onChange={(e) => setApplicantId(e.target.value)} />
          </Field>
        </div>
        <Field label="Full name">
          <input className="field-input" value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="District">
            <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} />
          </Field>
          <Field label="Phone (optional)">
            <input className="field-input" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Field>
        </div>
        <div>
          <div className="field-label">Production types</div>
          <div className="flex flex-wrap gap-2">
            {PRODUCTION_TYPES.map((t) => (
              <label key={t} className={`cursor-pointer rounded-lg border px-2.5 py-1 text-xs font-medium ${productionTypes.includes(t) ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-300 text-slate-600'}`}>
                <input type="checkbox" className="mr-1.5" checked={productionTypes.includes(t)} onChange={() => toggle(t)} />
                {t.replaceAll('_', ' ')}
              </label>
            ))}
          </div>
        </div>
        <button className="btn-primary w-full" disabled={busy || !applicantId || !fullName || !district || productionTypes.length === 0} onClick={submit}>
          {busy ? 'Submitting…' : 'Submit registration (raises SeyPay fee intent)'}
        </button>
      </div>
    </Modal>
  );
}
